/* =========================================
   GeoTargeting.js
   Central logic for:
   - store popup by storeId
   - no-retailer fallback
   - utility helpers
   ========================================= */

(() => {
  const GeoTargeting = {
    getConfig() {
      return window.GeoTargetingConfig || {};
    },

    getLocations() {
      return window.GeoTargetingLocations || {};
    },

    getStoreById(storeId) {
      if (!storeId || typeof storeId !== "string") return null;
      return this.getLocations()[storeId] || null;
    },

    getPopupUtility() {
      return window.RTQPopups &&
        typeof window.RTQPopups.runStorePopup === "function"
        ? window.RTQPopups
        : null;
    },

    getRepeatPopupDelayMs() {
      const config = this.getConfig();
      const days = Number(config.repeatPopupDelayDays);

      if (!Number.isFinite(days) || days <= 0) return 0;

      return days * 24 * 60 * 60 * 1000;
    },

    getRepeatPopupStorageKey() {
      const config = this.getConfig();
      return (
        config.repeatPopupStorageKey || "GeoTargeting_store_popup_last_shown_at"
      );
    },

    getLastPopupShownAt() {
      try {
        const value = window.localStorage.getItem(this.getRepeatPopupStorageKey());
        const timestamp = Number(value);

        return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
      } catch (error) {
        return 0;
      }
    },

    setLastPopupShownAt(timestamp) {
      try {
        window.localStorage.setItem(
          this.getRepeatPopupStorageKey(),
          String(timestamp)
        );
      } catch (error) {}
    },

    shouldDelayPopup() {
      const delayMs = this.getRepeatPopupDelayMs();
      if (!delayMs) return false;

      const lastShownAt = this.getLastPopupShownAt();
      if (!lastShownAt) return false;

      return Date.now() - lastShownAt < delayMs;
    },

    isMobile() {
      if (
        navigator.userAgentData &&
        typeof navigator.userAgentData.mobile === "boolean"
      ) {
        return navigator.userAgentData.mobile;
      }

      if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
        return true;
      }

      const ua = navigator.userAgent || "";
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi/i.test(
        ua
      );
    },

    normalizeStore(store) {
      const config = this.getConfig();
      const defaults = config.defaults || {};
      const popup = config.popup || {};

      return {
        storeId: store.storeId || "",
        title: store.title || config.defaultPopupTitle || "Find Us In Store",
        storeName: store.storeName || "",
        address1: store.address1 || defaults.address1 || "",
        address2: store.address2 || defaults.address2 || "",
        phone: store.phone || defaults.phone || "",
        notes: store.notes || defaults.notes || "",
        storePhotoUrl: store.storePhotoUrl || defaults.storePhotoUrl || "",
        mainImageUrl: store.mainImageUrl || defaults.mainImageUrl || "",
        directionsUrl: store.directionsUrl || defaults.directionsUrl || "#",
        directionsButtonText:
          store.directionsButtonText ||
          config.defaultDirectionsButtonText ||
          "Get directions",
        height: store.height || popup.height || "80vh",
        width: store.width || popup.width || "min(1000px, 92vw)",
        titleMaxPx: store.titleMaxPx || popup.titleMaxPx || 44,
        titleMinPx: store.titleMinPx || popup.titleMinPx || 18
      };
    },

    runNoRetailerFallback() {
      const config = this.getConfig();
      const fallback = config.fallback || {};

      const key = fallback.urlParamKey || "utm_content";
      const desktopValue = fallback.desktopValue || "nrDesktop";
      const mobileValue = fallback.mobileValue || "nrMobile";

      const value = this.isMobile() ? mobileValue : desktopValue;
      const url = new URL(window.location.href);

      if (url.searchParams.get(key) === value) return;

      url.searchParams.set(key, value);
      window.history.replaceState({}, "", url.toString());
    },

    runStorePopupById(storeId) {
      const popupUtility = this.getPopupUtility();
      const store = this.getStoreById(storeId);

      if (!store) {
        console.warn(`[GeoTargeting] No store found for storeId: ${storeId}`);
        this.runNoRetailerFallback();
        return;
      }

      if (!popupUtility) {
        console.warn("[GeoTargeting] Popup utility is not available.");
        return;
      }

      if (this.shouldDelayPopup()) return;

      const config = this.getConfig();
      const s = this.normalizeStore(store);

      this.setLastPopupShownAt(Date.now());

      popupUtility.runStorePopup({
        title: s.title,

        storeName: s.storeName,
        address1: s.address1,
        address2: s.address2,
        phone: s.phone,
        notes: s.notes,

        mainImageUrl: s.mainImageUrl,
        storePhotoUrl: s.storePhotoUrl,

        directionsUrl: s.directionsUrl,
        directionsButtonText: s.directionsButtonText,

        height: s.height,
        width: s.width,

        titleMaxPx: s.titleMaxPx,
        titleMinPx: s.titleMinPx,

        announcement: {
          text: s.title,
          linkText: s.directionsButtonText,
          linkUrl: s.directionsUrl
        },

        sessionCookieName:
          config.sessionCookieName || "GeoTargeting_store_popup_opened_session"
      });
    }
  };

  window.GeoTargeting = GeoTargeting;
  window.GeoTargetingRunStorePopupById = function (storeId) {
    GeoTargeting.runStorePopupById(storeId);
  };
  window.GeoTargetingRunNoRetailerFallback = function () {
    GeoTargeting.runNoRetailerFallback();
  };
})();
